import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useErrorHandler } from '~/shared/utils/errorBoundary/hooks/useErrorHandler';
import { MutationKeys, QueryKeys, ScopedMutationKeys } from '~/services/loyalty/constants';
import { useBasket } from '~/features/basket';
import { useTranslation } from '~/shared/utils/translation';
import { useFrame } from '~/shared/utils';
import { useEvents } from '../../useEvents';

export const useReserveVoucher = () => {
    const { trackAndDisplayError: trackError } = useErrorHandler();
    const { applyPromoCode } = useBasket();
    const queryClient = useQueryClient();
    const { data: frame } = useFrame();
    const { reserveVoucherEvent } = useEvents(frame);
    const { translate } = useTranslation();

    type Barcode = string;

    return useMutation<Barcode, Error, Barcode>({
        mutationKey: [MutationKeys.RESERVE_VOUCHER],
        mutationFn: async (barcode: Barcode) => {
            if (!barcode) throw new Error(translate('basket.missingCoupon'));
            await applyPromoCode(barcode);
            return barcode;
        },
        // scope is to ensure that the mutation runs in serial instead of parallel to
        // ensure full sync between FE and SFCC when adding a voucher
        scope: {
            id: ScopedMutationKeys.SCOPED_VOUCHERS,
        },
        onError: () => {
            const error = new Error(translate('basket.couldNotSaveChangesError'));
            trackError(error);
            throw error;
        },
        onSuccess: (_, promoId) => {
            queryClient.invalidateQueries({
                queryKey: [QueryKeys.ACTIVE_VOUCHERS],
            });

            reserveVoucherEvent(promoId);
        },
    });
};
